import { SwapStatus } from '@layerswap/widget-types';
import { hmsToMs } from '../components/utils/formatTime';
/** Must stay below HOT_INTERVAL_MS so SWR dedup never swallows a scheduled poll. */
export const SWAP_POLL_DEDUPE_MS = 200;
const HOT_INTERVAL_MS = 300;
/** How long after a trigger (tx submit / payload change) polling stays at the hot interval. */
const HOT_WINDOW_MS = 6000;
/** A swap with no payload change for this long is an outlier — relax polling. */
const MIN_OUTLIER_THRESHOLD_MS = 60000;
export function resolveSwapPollingInterval(input) {
    const { swap, refuelRequired, now, lastChangeAt, txSubmittedAt, avgCompletionTime } = input;
    if (!swap?.status)
        return input.hasError ? 5000 : 1000;
    if ([SwapStatus.Failed, SwapStatus.Expired, SwapStatus.Refunded].includes(swap.status))
        return 0;
    const outputReady = swap.transactions?.some(t => t.type === 'output' && t.transaction_hash && t.amount);
    const refuelReady = !refuelRequired || swap.transactions?.some(t => t.type === 'refuel' && t.transaction_hash && t.amount);
    // Backend completion and settlement data are both required. Early UI completion must
    // not hide the eventual backend transition, and completed can precede output/refuel data.
    if (swap.status === SwapStatus.Completed && outputReady && refuelReady)
        return 0;
    const sinceActivity = Math.max(0, now - Math.max(lastChangeAt, txSubmittedAt ?? 0));
    const awaitingDeposit = (swap.status === SwapStatus.Created || swap.status === SwapStatus.UserTransferPending)
        && !swap.transactions?.some(t => t.type === 'input') && txSubmittedAt == null;
    if (awaitingDeposit) {
        // Wallet flow before the user signs: nothing changes server-side until they act.
        if (!swap.use_deposit_address)
            return withJitter(stepInterval(sinceActivity, [[30000, 3000], [120000, 5000]], 10000));
        // Deposit-address flow: the server can detect an incoming transfer at any moment.
        return withJitter(stepInterval(sinceActivity, [[60000, 1000], [180000, 2000]], 5000));
    }
    if (swap.status === SwapStatus.PendingRefund)
        return withJitter(stepInterval(sinceActivity, [[60000, 2000]], 5000));
    // Hot phases (InputPending / OutputPending / SettlingOutput): completion is expected
    // within seconds of the last movement, so hold the floor while the payload is changing
    // and decay only once it stalls.
    if (sinceActivity < HOT_WINDOW_MS)
        return withJitter(HOT_INTERVAL_MS);
    const avgMs = hmsToMs(avgCompletionTime);
    const outlierAt = Math.max(MIN_OUTLIER_THRESHOLD_MS, avgMs ? avgMs * 2 : 0);
    if (sinceActivity > outlierAt * 5)
        return withJitter(10000);
    if (sinceActivity > outlierAt)
        return withJitter(5000);
    return withJitter(stepInterval(sinceActivity, [[15000, 1000]], 2000));
}
function stepInterval(elapsedMs, steps, fallbackMs) {
    for (const [below, interval] of steps) {
        if (elapsedMs < below)
            return interval;
    }
    return fallbackMs;
}
/** ±15% so simultaneously-started clients don't synchronize into request spikes. */
function withJitter(ms) {
    return Math.round(ms * (0.85 + Math.random() * 0.3));
}
